/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { buttonStyles, SearchForVendorsContainer } from './AddEditOneTimeJob.Style';
import {
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
  InputProps,
  Autocomplete,
  Stack,
  styled,
} from '@mui/material';
import { debounce } from 'lodash';

import { getStoredCustomerDetails, itemsPerPage } from '../../../../Shared/Utilities/utils';
import { StyledPagination } from '../../../../Shared/Components/Common/CommonStyle/Pagination.Style';

import { Icons } from '../../../../Shared/Constants/Icons';

import JobCreateApiService from '../../Services/JobCreateService';
import { dataStatus } from '../../../../API';
import { IVendor } from '../../Models/AddEditOneTimeJob.Model';
import theme from '../../../../Shared/Themes/theme';

interface ISearchVendorProps {
  selectedVendors: IVendor[];
  isMulti: boolean;
  onVendorSelect: (v: IVendor[]) => void;
  fieldName: string;
  inPopup?: boolean;
}

interface OptionStyle {
  opacity: string;
  pointerEvents: 'none' | 'all';
}

const CustomListbox = styled('ul')({
  borderRadius: '1rem',
  padding: 0,
  margin: 0,
  position: 'relative',
});

const SearchVendor = (props: ISearchVendorProps): JSX.Element => {
  const { t } = useTranslation(['oneTimeJob']);
  const { selectedVendors, onVendorSelect, isMulti, fieldName } = props;
  const [vendorsList, setVendorsList] = useState<IVendor[]>([]);
  const [chosenVendors, setChosenVendors] = useState<IVendor[]>(selectedVendors);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteIcons, setShowDeleteIcons] = useState(Array(chosenVendors.length).fill(true));
  const [isAddVendorDisabled, setIsAddVendorDisabled] = useState(false);
  const [labels, setLabels] = useState<string[]>(['Select Vendor']);
  const [isOptionSelected, setIsOptionSelected] = useState<boolean[]>([false]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValues, setInputValues] = useState<string[]>(Array(selectedVendors.length).fill(''));

  const { textField, addButton, addButtonText } = buttonStyles;
  const cancelIconStyle = { cursor: 'pointer', width: '1.5rem', height: '1.5rem' };
  const IconStyle = { cursor: 'pointer' };
  const { userId = '' } = getStoredCustomerDetails() || {};

  const fetchVendors = useCallback(
    async (term: string, page: number) => {
      setIsLoading(true);
      try {
        const response = await JobCreateApiService.allVendorsOnARKPagination(
          '',
          dataStatus.A,
          term,
          itemsPerPage,
          page,
          userId
        );
        const modifiedResponse = (response?.data ?? []).map(
          (item: { vendorId: string; vendorCompanyName: string; email: string }) => ({
            vendorName: item.vendorCompanyName,
            vendorId: item.vendorId,
            email: item.email,
          })
        );
        setVendorsList(modifiedResponse);
        setTotalCount(response?.metaData?.totalCount ?? 0);
      } catch (e) {
        console.error('Error fetching vendors', e);
        setVendorsList([]);
      }
      setIsLoading(false);
    },
    [userId]
  );

  const debounceFetchVendors = useCallback(
    debounce((term: string) => {
      setCurrentPage(1);
      fetchVendors(term, 1);
    }, 500),
    [fetchVendors]
  );

  const handlePaginationChanges = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    fetchVendors(searchTerm, page);
  };

  const addVendor = (): void => {
    setChosenVendors([...chosenVendors, { vendorId: '', vendorName: '', index: chosenVendors.length, email: '' }]);
    setShowDeleteIcons([...showDeleteIcons, true]);
    setLabels([...labels, 'Select Vendor']);
    setIsOptionSelected([...isOptionSelected, false]);
    setInputValues([...inputValues, '']);
  };

  const handleDeleteVendor = (index: number): void => {
    const filteredVendorsList = chosenVendors.filter((vendorItem: IVendor) => vendorItem.index !== index);
    setChosenVendors(filteredVendorsList);
    onVendorSelect(filteredVendorsList);
    const newLabels = [...labels];
    newLabels.splice(index, 1);
    setLabels(newLabels);
    const newFlags = [...isOptionSelected];
    newFlags.splice(index, 1);
    setIsOptionSelected(newFlags);
    const newInputValues = [...inputValues];
    newInputValues.splice(index, 1);
    setInputValues(newInputValues);
  };

  const handleClearVendor = (index: number): void => {
    const modifiedVendors = chosenVendors.map((vendorItem: IVendor) => {
      if (vendorItem.index === index) {
        return { index, vendorId: '', vendorName: '', email: '' };
      }
      return vendorItem;
    });
    setChosenVendors(modifiedVendors);
    onVendorSelect(modifiedVendors);

    const newFlags = [...isOptionSelected];
    newFlags[index] = false;
    setIsOptionSelected(newFlags);

    const newShowDeleteIcons = [...showDeleteIcons];
    newShowDeleteIcons[index] = false;
    setShowDeleteIcons(newShowDeleteIcons);

    const newInputValues = [...inputValues];
    newInputValues[index] = '';
    setInputValues(newInputValues);

    setSearchTerm('');
    setCurrentPage(1);
    fetchVendors('', 1);
  };

  const handleInputChange = (_event: any, newInputValue: string, reason: string, index: number): void => {
    const updated = [...inputValues];
    updated[index] = newInputValue;
    setInputValues(updated);
    if (reason === 'input') {
      setSearchTerm(newInputValue);
      const newFlags = [...isOptionSelected];
      newFlags[index] = false;
      setIsOptionSelected(newFlags);
      debounceFetchVendors(newInputValue);
    }
  };

  const renderEndAdornment = (index: number): JSX.Element => {
    if (isOptionSelected[index]) {
      return (
        <>
          <IconButton onMouseDown={(e) => e.preventDefault()} onClick={() => handleClearVendor(index)}>
            <img src={Icons.CancelIcon} style={cancelIconStyle} />
          </IconButton>
          {index > 0 && (
            <IconButton onClick={() => handleDeleteVendor(index)}>
              <img src={Icons.DeleteIcon} alt={t('altTexts:delete')} style={IconStyle} />
            </IconButton>
          )}
        </>
      );
    }
    if (index > 0 && showDeleteIcons[index]) {
      return (
        <IconButton onClick={() => handleDeleteVendor(index)}>
          <img src={Icons.DeleteIcon} alt={t('altTexts:delete')} style={IconStyle} />
        </IconButton>
      );
    }
    if (inputValues[index]) {
      return (
        <IconButton onMouseDown={(e) => e.preventDefault()} onClick={() => handleClearVendor(index)}>
          <img src={Icons.CancelIcon} alt="clear" style={cancelIconStyle} />
        </IconButton>
      );
    }
    return <img src={Icons.SearchIcon} alt={t('altTexts:search')} style={IconStyle} />;
  };

  const getOptionStyle = (option: IVendor): OptionStyle => {
    const isVendorSelected = chosenVendors.some((vendor) => vendor.vendorName === option.vendorName);
    return {
      opacity: isVendorSelected ? '0.7' : '1',
      pointerEvents: isVendorSelected ? 'none' : 'all',
    };
  };

  const handleSearchVendorOnChange = (event: any, newValue: any, index: number): void => {
    const newShowDeleteIcons = [...showDeleteIcons];
    newShowDeleteIcons[index] = !!newValue;
    setShowDeleteIcons(newShowDeleteIcons);

    const modifiedVendors = chosenVendors.map((vendorItem: IVendor) => {
      if (vendorItem.index === index) {
        return {
          index,
          vendorId: newValue?.vendorId as string,
          vendorName: newValue?.vendorName as string,
          email: newValue?.email as string,
        };
      }
      return vendorItem;
    });
    setChosenVendors(modifiedVendors);
    onVendorSelect(modifiedVendors);

    const newFlags = [...isOptionSelected];
    newFlags[index] = true;
    setIsOptionSelected(newFlags);
  };

  useEffect(() => {
    fetchVendors('', 1);
  }, []);

  useEffect(() => {
    setChosenVendors(selectedVendors);
  }, [selectedVendors]);

  useEffect(() => {
    const emptyVendors = chosenVendors.filter((vendorItem) => vendorItem.vendorId === '');
    setIsAddVendorDisabled(emptyVendors.length > 0);
  }, [chosenVendors]);

  useEffect(() => {
    const updatedLabels = [...labels];
    updatedLabels[labels.length - 1] = isOptionSelected[labels.length - 1]
      ? `${t('oneTimeJob:selectedVendor')}`
      : `${t('oneTimeJob:selectVendor')}`;
    setLabels(updatedLabels);
  }, [isOptionSelected]);

  return (
    <>
      <SearchForVendorsContainer
        sx={{
          [theme.breakpoints.down('laptop')]: {
            display: 'grid',
            gridTemplateColumns: props?.inPopup ? 'auto' : 'repeat(2,1fr)',
          },
        }}>
        {chosenVendors.map((vendorItem, index) => (
          <Box key={index} sx={{ ...textField, marginBottom: '2rem', position: 'relative' }}>
            <Autocomplete
              options={vendorsList}
              getOptionLabel={(option: IVendor) => option.vendorName}
              filterOptions={(options) => options}
              clearOnBlur={false}
              value={isOptionSelected[index] ? chosenVendors.find((v) => v.index === index) ?? null : null}
              inputValue={inputValues[index] ?? ''}
              onInputChange={(_event, newInputValue, reason) => handleInputChange(_event, newInputValue, reason, index)}
              noOptionsText={
                isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', padding: '0.5rem' }}>
                    <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                  </Box>
                ) : (
                  t('jobManagement:noOptionsFound')
                )
              }
              onChange={(event, newValue) => handleSearchVendorOnChange(event, newValue, index)}
              componentsProps={{
                paper: {
                  sx: { borderRadius: '1rem', overflow: 'hidden' },
                },
              }}
              ListboxComponent={(listboxProps) => (
                <CustomListbox>
                  <Box sx={{ position: 'relative' }}>
                    <ul
                      {...listboxProps}
                      style={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        padding: 0,
                        margin: 0,
                        opacity: isLoading ? 0.4 : 1,
                      }}>
                      {listboxProps.children}
                    </ul>
                    {isLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}>
                        <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                      </Box>
                    )}
                  </Box>
                  {totalCount > itemsPerPage && (
                    <Stack
                      spacing={2}
                      justifyContent="center"
                      onMouseDown={(e) => e.preventDefault()}
                      sx={{ padding: '0.5rem', borderTop: `1px solid ${theme.palette.divider}` }}>
                      <StyledPagination
                        count={Math.ceil(totalCount / itemsPerPage)}
                        page={currentPage}
                        onChange={handlePaginationChanges}
                        color="primary"
                      />
                    </Stack>
                  )}
                </CustomListbox>
              )}
              renderInput={(params) => (
                <TextField
                  name={fieldName}
                  sx={{ ...textField }}
                  {...params}
                  label={
                    <span>
                      {labels[index]} <span className="mandatory-field">*</span>
                    </span>
                  }
                  InputProps={{
                    ...(params.InputProps as InputProps),
                    startAdornment: inputValues[index] ? (
                      <InputAdornment position="start">
                        <img src={Icons.SearchIcon} alt={t('altTexts:search')} style={IconStyle} />
                      </InputAdornment>
                    ) : null,
                    endAdornment: <InputAdornment position="end">{renderEndAdornment(index)}</InputAdornment>,
                  }}
                />
              )}
              renderOption={(props, option: IVendor, state) => (
                <div>
                  <li
                    {...props}
                    className={`option ${state.selected ? 'selectedOption' : ''}`}
                    style={{ ...getOptionStyle(option) }}>
                    <div>
                      {option.vendorName}
                      <img src={Icons.PlusIcon} className="plusIcon" />
                    </div>
                  </li>
                  {option !== vendorsList[vendorsList.length - 1] && <hr className="optionDivider" />}
                </div>
              )}
            />
          </Box>
        ))}
        {isMulti && (
          <Button
            variant="outlined"
            sx={{ ...addButton, opacity: isAddVendorDisabled ? 0.5 : 1, height: '3.5rem' }}
            disabled={isAddVendorDisabled}
            onClick={addVendor}>
            <img src={Icons.AddIcon} alt={t('altTexts:addIcon')} />
            <Typography sx={{ ...addButtonText, textTransform: 'none' }}>{t('oneTimeJob:addAnotherVendor')}</Typography>
          </Button>
        )}
      </SearchForVendorsContainer>
    </>
  );
};

export default SearchVendor;
